// Learnosity's own asset store plus the PL-owned CDNs. An <img src> pointing anywhere
// else got there by paste or by the editor's "Source URL" field, which is what
// PLP-6185 blocks and PLP-6191 backfills.
const ALLOWED_IMAGE_SOURCE_PREFIXES = [
  "https://assets.learnosity.com/",
  "https://media.educationgalaxy.com/",
  "https://media.progresslearning.com/",
  "https://media-dev.progresslearning.com/",
];

const IMG_TAG_PATTERN = /<img\b[^>]*>/gi;
const IMG_SRC_PATTERN = /\bsrc\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i;

export const isAllowedImageSource = (source: string): boolean => {
  const trimmed = source.trim().toLowerCase();
  if (!trimmed) return true;
  return ALLOWED_IMAGE_SOURCE_PREFIXES.some((prefix) =>
    trimmed.startsWith(prefix)
  );
};

const collectImageSources = (html: string): string[] => {
  const sources: string[] = [];
  const tags = html.match(IMG_TAG_PATTERN) ?? [];
  tags.forEach((tag) => {
    const match = IMG_SRC_PATTERN.exec(tag);
    const source = match?.[1] ?? match?.[2] ?? match?.[3];
    if (source) sources.push(source);
  });
  return sources;
};

// Learnosity spreads rich text across stimulus, options, rationales, sample answers
// and paginated passage content, so walk the whole widget instead of naming fields.
export const findDisallowedImageSources = (content: unknown): string[] => {
  const disallowed: string[] = [];
  const pending: unknown[] = [content];
  while (pending.length > 0) {
    const node = pending.pop();
    if (typeof node === "string") {
      collectImageSources(node).forEach((source) => {
        if (!isAllowedImageSource(source)) disallowed.push(source);
      });
    } else if (Array.isArray(node)) {
      pending.push(...node);
    } else if (node && typeof node === "object") {
      pending.push(...Object.values(node));
    }
  }
  return disallowed;
};

export const hasDisallowedImageSources = (content: unknown): boolean =>
  findDisallowedImageSources(content).length > 0;

// The save path hands Learnosity payloads around as JSON strings; parse before
// walking or the escaped quotes make every src look external.
export const hasDisallowedImageSourcesInRequest = (
  request: string
): boolean => {
  if (!request) return false;
  try {
    return hasDisallowedImageSources(JSON.parse(request));
  } catch {
    // A malformed payload is the save path's problem to report, not this guard's.
    return false;
  }
};
